import { useEffect, useState } from "react";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useFinancialYear } from "../context/FinancialYearContext";

const getVal = (x, ...keys) => {
  for (const k of keys) {
    if (x?.[k] !== undefined && x?.[k] !== null) return x[k];
  }
  return "";
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const styles = {
  page: { padding: 0, fontSize: 11 },
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3 },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  stat: {
    borderRadius: 10,
    border: "1px solid #e9ecef",
    background: "#f8f9fa",
    padding: 9,
    marginBottom: 8,
  },
  tableWrap: {
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, marginBottom: 0, minWidth: 650 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    color: "#fff",
    background: "#07111f",
  },
  td: { fontSize: 11, padding: "6px 8px", whiteSpace: "nowrap" },
};

export default function CustomerOutstandingPage() {
  const { user } = useAuth();
  const { financialYear } = useFinancialYear();

  const [rows, setRows] = useState([]);
  const [fromDate, setFromDate] = useState(financialYear.fromDate);
  const [toDate, setToDate] = useState(financialYear.toDate);
  const [loading, setLoading] = useState(false);

  const companyId = user?.companyId || localStorage.getItem("companyId");

  useEffect(() => {
    setFromDate(financialYear.fromDate);
    setToDate(financialYear.toDate);
  }, [financialYear.fromDate, financialYear.toDate]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialYear.fromDate, financialYear.toDate, companyId]);

  const load = async () => {
    try {
      setLoading(true);

      const params = {
        fromDate,
        toDate,
      };

      if (companyId) params.companyId = companyId;

      const res = await client.get("/finance/customer-outstanding", {
        params,
      });

      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      alert(
        err?.response?.data?.message ||
          err?.response?.data ||
          "Failed to load customer outstanding",
      );
    } finally {
      setLoading(false);
    }
  };

  const total = rows.reduce(
    (s, r) => s + num(getVal(r, "Balance", "balance")),
    0,
  );

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>
            <i className="bi bi-person-lines-fill me-2"></i>
            Customer Outstanding / Receivables
          </div>
          <div style={styles.sub}>
            Customer-wise pending receivable balances for selected financial
            year
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 align-items-end mb-2">
            <Field label="From Date" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Field>

            <Field label="To Date" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Field>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-success btn-sm"
                style={styles.btn}
                onClick={load}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Load"}
              </button>
            </div>
          </div>

          <div style={styles.stat}>
            Total Receivable:{" "}
            <strong style={{ color: "#0f766e" }}>₹ {money(total)} Dr</strong>
            <span className="ms-3 text-muted">
              Period: {fromDate} to {toDate}
            </span>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Account</th>
                  <th className="text-end" style={styles.th}>
                    Receivable
                  </th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="3"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      {loading ? "Loading..." : "No outstanding"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => {
                    const balance = num(getVal(r, "Balance", "balance"));

                    return (
                      <tr key={i}>
                        <td style={styles.td}>
                          {getVal(r, "AccountCode", "accountCode")}
                        </td>

                        <td style={styles.td}>
                          {getVal(r, "AccountName", "accountName")}
                        </td>

                        <td
                          className="text-end"
                          style={{
                            ...styles.td,
                            fontWeight: 700,
                            color: "#0f766e",
                          }}
                        >
                          {money(balance)} Dr
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

              <tfoot>
                <tr style={{ background: "#f8f9fa", fontWeight: 700 }}>
                  <td colSpan="2" className="text-end" style={styles.td}>
                    Total
                  </td>

                  <td
                    className="text-end"
                    style={{ ...styles.td, color: "#0f766e" }}
                  >
                    {money(total)} Dr
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}
